import { ApiService } from '../apiService'
import type {
  AdminNarrationTagCategoryDto,
  AdminNarrationTagDto,
  AdminNarrationVoiceDto,
  ElevenLabsVoiceInfo,
  ElevenLabsVoicePage,
  ElevenLabsVoiceQuery,
  NarrationGenerationRow,
  NarrationPaged,
  NarrationUsageQuery,
  NarrationUsageSummary,
  NarrationUsageTarget,
  NarrationUsageUser,
  SaveNarrationTagCategoryRequest,
  SaveNarrationTagRequest,
  SaveNarrationVoiceRequest,
} from '../types/narration'

/**
 * AI narration lists on the Admin API (Admin only). Validation failures come back as 400
 * `{ message }`, written to be shown as is. Listing the ElevenLabs account's voices is 503 when no
 * key is configured (`ElevenLabs__ApiKey` in admin.env) or ElevenLabs refuses it.
 */
export const narrationApi = {
  voices: {
    list: () => ApiService.get<AdminNarrationVoiceDto[]>('/admin/narration/voices'),
    create: (request: SaveNarrationVoiceRequest) =>
      ApiService.post<AdminNarrationVoiceDto>('/admin/narration/voices', request),
    update: (id: string, request: SaveNarrationVoiceRequest) =>
      ApiService.put<AdminNarrationVoiceDto>(`/admin/narration/voices/${id}`, request),
    /** Hard delete: scripts that name the voice need a new one picked. */
    remove: (id: string) => ApiService.delete<void>(`/admin/narration/voices/${id}`),
  },

  /**
   * ElevenLabs voices to pick from: the account's ("My voices") or the public Voice Library. 503 when
   * no key is configured or ElevenLabs refuses it, 429 when it is rate limiting.
   */
  elevenLabsVoices: {
    search: (q: ElevenLabsVoiceQuery) =>
      ApiService.get<ElevenLabsVoicePage>(`/admin/narration/elevenlabs-voices${elevenLabsQueryString(q)}`),
    /** One voice by id, account first then the library (isInAccount false). 404 when unknown. */
    get: (voiceId: string) =>
      ApiService.get<ElevenLabsVoiceInfo>(`/admin/narration/elevenlabs-voices/${encodeURIComponent(voiceId)}`),
    /** Save a library voice to the account so narration can use it; returns it with isInAccount true. */
    add: (voiceId: string, body: { publicOwnerId?: string | null; name?: string | null }) =>
      ApiService.post<ElevenLabsVoiceInfo>(`/admin/narration/elevenlabs-voices/${encodeURIComponent(voiceId)}/add`, body),
  },

  tags: {
    list: () => ApiService.get<AdminNarrationTagDto[]>('/admin/narration/tags'),
    create: (request: SaveNarrationTagRequest) => ApiService.post<AdminNarrationTagDto>('/admin/narration/tags', request),
    update: (id: string, request: SaveNarrationTagRequest) =>
      ApiService.put<AdminNarrationTagDto>(`/admin/narration/tags/${id}`, request),
    remove: (id: string) => ApiService.delete<void>(`/admin/narration/tags/${id}`),
  },

  /** Palette groups for the tags. Deleting one leaves its tags uncategorised. */
  tagCategories: {
    list: () => ApiService.get<AdminNarrationTagCategoryDto[]>('/admin/narration/tag-categories'),
    create: (request: SaveNarrationTagCategoryRequest) =>
      ApiService.post<AdminNarrationTagCategoryDto>('/admin/narration/tag-categories', request),
    update: (id: string, request: SaveNarrationTagCategoryRequest) =>
      ApiService.put<AdminNarrationTagCategoryDto>(`/admin/narration/tag-categories/${id}`, request),
    /** Every category id in the new order; returns the list re-numbered 10, 20, …. */
    reorder: (ids: string[]) =>
      ApiService.put<AdminNarrationTagCategoryDto[]>('/admin/narration/tag-categories/order', { ids }),
    remove: (id: string) => ApiService.delete<void>(`/admin/narration/tag-categories/${id}`),
  },

  /** The usage ledger report. A bad month is 400 `{ message }`. */
  usage: {
    summary: (q: NarrationUsageQuery) =>
      ApiService.get<NarrationUsageSummary>(`/admin/narration/usage/summary${usageQueryString(q)}`),
    users: (q: NarrationUsageQuery) =>
      ApiService.get<NarrationPaged<NarrationUsageUser>>(`/admin/narration/usage/users${usageQueryString(q)}`),
    targets: (q: NarrationUsageQuery) =>
      ApiService.get<NarrationPaged<NarrationUsageTarget>>(`/admin/narration/usage/targets${usageQueryString(q)}`),
    generations: (q: NarrationUsageQuery) =>
      ApiService.get<NarrationPaged<NarrationGenerationRow>>(`/admin/narration/usage/generations${usageQueryString(q)}`),
  },
}

/** `?month=…&userId=…&search=…&page=…&pageSize=…`, leaving out what is not set. */
export function usageQueryString(q: NarrationUsageQuery): string {
  const params = new URLSearchParams()
  if (q.month) params.set('month', q.month)
  if (q.userId) params.set('userId', q.userId)
  if (q.search) params.set('search', q.search)
  if (q.page) params.set('page', String(q.page))
  if (q.pageSize) params.set('pageSize', String(q.pageSize))
  const text = params.toString()
  return text ? `?${text}` : ''
}

/** `?source=…&search=…`, leaving out what is blank. */
export function elevenLabsQueryString(q: ElevenLabsVoiceQuery): string {
  const params = new URLSearchParams()
  params.set('source', q.source)
  const optional: (keyof ElevenLabsVoiceQuery)[] = ['search', 'language', 'gender', 'accent', 'age', 'category', 'useCase', 'page']
  for (const key of optional) {
    const value = q[key]
    if (typeof value === 'string' && value.trim() !== '') params.set(key, value.trim())
  }
  if (q.pageSize) params.set('pageSize', String(q.pageSize))
  return `?${params.toString()}`
}
